// src/modules/wiring/hooks/useWireRotateOffset.js
//
// H5 Aşama 1: useComponentWiring'den çıkarıldı.
// "R" tuşu ile component (priz/anahtar) preview sembolünün rotasyonunu
// 90 derece artırarak günceller. Sadece UI rotation state'ini yönetir;
// yerleştirme/wiring akışına dokunmaz.

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { setCommandMessage } from '../../../redux/operationSlice';

/**
 * @param {object} args
 * @param {THREE.Scene} args.scene
 * @param {object} args.base - useBaseWiring'in döndüğü API (ref'ler + createSymbol + dispatch)
 * @param {object} args.options - Tüketici hook'un options (itemTypeName: 'lightSwitch' veya 'socket')
 * @returns {{ rotateOffsetRef: React.MutableRefObject<number> }}
 */
export function useWireRotateOffset({ scene, base, options = {} }) {
  const rotateOffsetRef = useRef(0);
  // `base` her render yeni obje (useBaseWiring her seferinde yeni dönüş üretir).
  // Bunu useEffect dependency'sine koyarsak handler sürekli register/unregister
  // edilir ve R event'i bazen yakalanmaz. Ref ile sarıp güncel kopyayı saklıyoruz.
  const baseRef = useRef(base);
  baseRef.current = base;
  const itemTypeNameRef = useRef(options.itemTypeName);
  itemTypeNameRef.current = options.itemTypeName;

  useEffect(() => {
    if (!scene) return;

    const handleKeyDown = (e) => {
      if (e.key !== 'r') return;
      const base = baseRef.current;
      if (!base) return;

      rotateOffsetRef.current = (rotateOffsetRef.current + 90) % 360;
      const rotateLabel = itemTypeNameRef.current === 'lightSwitch'
        ? 'Anahtar rotasyonu'
        : 'Priz rotasyonu';
      base.dispatch(setCommandMessage(`${rotateLabel}: ${rotateOffsetRef.current}°`));

      try {
        if (base.previewSymbolRef.current) {
          scene.remove(base.previewSymbolRef.current);
          base.previewSymbolRef.current.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
          });
          base.previewSymbolRef.current = null;
        }
        if (base.verticesRef.current.length > 0 && base.previewEndPointRef.current) {
          const lastPoint = base.verticesRef.current[base.verticesRef.current.length - 1];
          const directionVector = new THREE.Vector3()
            .subVectors(base.previewEndPointRef.current, lastPoint)
            .normalize();
          const angle = Math.atan2(directionVector.y, directionVector.x);
          const symbolPreview = base.createSymbol(
            base.previewEndPointRef.current,
            directionVector,
            angle,
            true,
            base.itemTypeRef.current,
            { rotateValue: rotateOffsetRef.current }
          );
          scene.add(symbolPreview);
          base.previewSymbolRef.current = symbolPreview;
        }
      } catch {
        // Preview rotation update failed — sessizce geç
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [scene]);

  return { rotateOffsetRef };
}

export default useWireRotateOffset;
